import React, { useState, useEffect } from 'react';
import Select, { MultiValue } from 'react-select';
import '../../styles/empreendimentosStyle/FiltrosLateraisEmpreendimentos.css';
import { ChevronUp, ChevronDown } from 'lucide-react';
import type { FiltrosSemPesquisa } from './Filtros';
import { getEmpreendimentosPaginados, getEstados, getMunicipios, getBairros, FiltrosEmpreendimentos } from '../../API/empreendimentosService';
import { authService } from '../../API/auth';

interface Props {
  filtros: FiltrosSemPesquisa;
  onChange: (novosFiltros: Partial<FiltrosSemPesquisa>) => void;
  setEmpreendimentos: (data: any[]) => void;
}

// --- Mock fixo dos filtros ---
const filtrosPlanoMock = {
  tipo: ['casa', 'apartamento', 'comercial', 'compacto', 'lote'],
  fase: ['planta', 'obra', 'pronto'],
  segmento: ['econômico', 'médio', 'alto', 'luxo', 'alto luxo', 'MCMV'],
  quartos: [1, 2, 3, 4, 5],
  suites: [1, 2, 3],
};

// --- Mock do plano do usuário ---

interface Estado { id: number; sigla: string; nome: string; }
interface Municipio { id: number; nome: string; uf: string; nome_original: string; }
interface Bairro { id: number; nome: string; municipio: Municipio; nome_original: string; }

const FiltrosLateraisEmpreendimentos: React.FC<Props> = ({ filtros, onChange, setEmpreendimentos }) => {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({
    preco: false, quartos: false, suites: false, metragem: false, tipo: false, fase: false, segmento: false
  });

  const [estados, setEstados] = useState<Estado[]>([]);
  const [municipios, setMunicipios] = useState<Municipio[]>([]);
  const [bairros, setBairros] = useState<Bairro[]>([]);
  const [planoUsuario, setPlanoUsuario] = useState<string[]>([]);
  const [filtrosLocais, setFiltrosLocais] = useState<FiltrosSemPesquisa>(filtros);

  // --- Disparar pesquisa sempre que filtrosLocais mudar ---
  useEffect(() => {
    pesquisarEmpreendimentos();
    onChange(filtrosLocais);
  }, [filtrosLocais]);

  useEffect(() => {
    async function carregarPlano() {
      try {
        const profile = await authService.getProfile();
        const segmentosUsuario = profile?.empresa_membership?.segmentos_acesso ?? [];
        setPlanoUsuario(segmentosUsuario);
        console.log("🔐 Plano do usuário carregado:", segmentosUsuario);
      } catch (err) {
        console.error("Erro ao carregar perfil:", err);
      }
    }

    carregarPlano();
  }, []);

  // --- Buscar estados ---
  useEffect(() => {
    const fetchEstados = async () => {
      try {
        const data = await getEstados();
        setEstados(data);
      } catch (err) { console.error(err); }
    };
    fetchEstados();
  }, []);

  // --- Buscar municípios ---
  useEffect(() => {
    if (!filtrosLocais.estado) {
      setMunicipios([]);
      onChange({ municipio: [], bairro: [] });
      return;
    }
    const fetchMunicipios = async () => {
      try {
        const estadoSelecionado = estados.find(e => e.nome === filtrosLocais.estado);
        if (!estadoSelecionado) return;
        const data = await getMunicipios(estadoSelecionado.id);
        setMunicipios(data);
      } catch (err) {
        console.error(err);
      }
    };
    fetchMunicipios();
  }, [filtrosLocais.estado, estados]);

  // --- Buscar bairros ---
  useEffect(() => {
    if (!filtrosLocais.municipio?.length) {
      setBairros([]);
      onChange({ bairro: [] });
      return;
    }
    const fetchBairros = async () => {
      try {
        const bairrosArray: Bairro[] = [];
        for (const municipioId of filtrosLocais.municipio) {
          const municipioObj = municipios.find(m => String(m.id) === municipioId);
          if (municipioObj) {
            const data = await getBairros(municipioObj.id);
            bairrosArray.push(...data);
          }
        }
        setBairros(bairrosArray);
      } catch (err) {
        console.error(err);
      }
    };
    fetchBairros();
  }, [filtrosLocais.municipio, municipios]);


  const toggleExpand = (key: string) => setExpanded(prev => ({ ...prev, [key]: !prev[key] }));

  const handleEstadoChange = (estado: string) => {
    setFiltrosLocais(prev => ({ ...prev, estado, municipio: [], bairro: [] }));
    onChange({ estado, municipio: [], bairro: [] });
  };

  const handleMunicipioChange = (newValue: MultiValue<{ value: string; label: string }>) => {
    const municipiosSelecionados = newValue.map(m => m.value); // agora são IDs
    setFiltrosLocais(prev => ({ ...prev, municipio: municipiosSelecionados, bairro: [] }));
    onChange({ municipio: municipiosSelecionados, bairro: [] });
  };

  const handleBairroChange = (newValue: MultiValue<{ value: string; label: string }>) => {
    const bairrosSelecionados = newValue.map(b => b.value); // agora IDs
    setFiltrosLocais(prev => ({ ...prev, bairro: bairrosSelecionados }));
    onChange({ bairro: bairrosSelecionados });
  };

  const handleNumeroChange = (valor: number, campo: keyof FiltrosSemPesquisa) => {
    setFiltrosLocais(prev => {
      const valoresAtuais = (prev[campo] as number[]) || [];
      const existe = valoresAtuais.includes(valor);
      const novosValores = existe 
        ? valoresAtuais.filter(v => v !== valor) 
        : [...valoresAtuais, valor];
      return { ...prev, [campo]: novosValores };
    });
  };


  const handleStringChange = (valor: string, campo: keyof FiltrosSemPesquisa) => {
    setFiltrosLocais(prev => {
      const valoresAtuais = (prev[campo] as string[]) || [];
      const existe = valoresAtuais.includes(valor);
      const novosValores = existe 
        ? valoresAtuais.filter(v => v !== valor) 
        : [...valoresAtuais, valor];
      const atualizado = { ...prev, [campo]: novosValores };
      onChange({ [campo]: novosValores });
      return atualizado;
    });
  };

  // --- Normalizador para acentos/maiúsculas ---
  const normalize = (str: string) =>
    str.toLowerCase()
       .normalize("NFD")
       .replace(/[\u0300-\u036f]/g, "")
       .trim();

  // --- Mapeamentos ---
  const mapTipoParaId: Record<string, number> = { 
    casa: 1, apartamento: 2, comercial: 3, compacto: 4 
  };

  const mapFaseParaId: Record<string, number> = { 
    planta: 1, obra: 2, pronto: 3 
  };

  const mapSegmentoParaId: Record<string, number> = { 
    economico: 1, medio: 2, alto: 3, luxo: 4, "alto luxo": 5, mcmv: 6 
  };

  // --- Função principal ---
  const pesquisarEmpreendimentos = async () => {
    try {
      const filtrosApi: FiltrosEmpreendimentos = {
        tipoEmpreendimento: filtrosLocais.tipoSelecionado?.length
          ? filtrosLocais.tipoSelecionado
              .map(t => {
                const norm = normalize(t);
                return mapTipoParaId[norm] ?? null;
              })
              .filter((v): v is number => v !== null)
          : undefined,

        faseEmpreendimento: filtrosLocais.faseSelecionada?.length
          ? filtrosLocais.faseSelecionada
              .map(f => {
                const norm = normalize(f);
                return mapFaseParaId[norm] ?? null;
              })
              .filter((v): v is number => v !== null)
          : undefined,

        segmento: filtrosLocais.segmentoSelecionado?.length
          ? filtrosLocais.segmentoSelecionado
              .map(s => {
                const norm = normalize(s);
                return mapSegmentoParaId[norm] ?? null;
              })
              .filter((v): v is number => v !== null)
          : undefined,

        quartos: filtrosLocais.quartos?.length ? filtrosLocais.quartos : undefined,
        suites: filtrosLocais.suites?.length ? filtrosLocais.suites : undefined,
        precoMin: filtrosLocais.precoMin ? Math.floor(filtrosLocais.precoMin) : undefined,
        precoMax: filtrosLocais.precoMax ? Math.floor(filtrosLocais.precoMax) : undefined,
        areaMin: filtrosLocais.metragemMin ? Math.floor(filtrosLocais.metragemMin) : undefined,
        areaMax: filtrosLocais.metragemMax ? Math.floor(filtrosLocais.metragemMax) : undefined,
        municipio: filtrosLocais.municipio?.length ? filtrosLocais.municipio : undefined,
        bairro: filtrosLocais.bairro?.length ? filtrosLocais.bairro : undefined,
      };

      console.log("🔎 Filtros enviados para API:", filtrosApi);

      const resposta = await getEmpreendimentosPaginados(filtrosApi);
      setEmpreendimentos(resposta.results);
    } catch (err) {
      console.error(err);
    }
  };

  // --- Habilitação de quartos/suítes ---
  const tipoSelecionadoPlano = filtros.tipoSelecionado?.[0] || '';
  const habilitaQuartosSuites = tipoSelecionadoPlano === 'casa' || tipoSelecionadoPlano === 'apartamento';

  // --- Tipos disponíveis ---
const tiposDisponiveis = filtrosPlanoMock.tipo.map(tipo => {
  const tipoLower = tipo.toLowerCase();
  const isCasaApartamento = ['casa', 'apartamento'].includes(tipoLower);

  if (isCasaApartamento) {
    return { valor: tipo, disabled: !planoUsuario.includes('casa_apartamento') };
  }

  return { valor: tipo, disabled: !planoUsuario.includes(tipoLower) };
});

// --- Segmentos disponíveis ---
  // Lógica baseada em união: se QUALQUER plano permitir, o segmento fica habilitado
  const segmentosDisponiveis = filtrosPlanoMock.segmento.map(seg => {
    const segNorm = normalize(seg);

    // começa desabilitado
    let habilitado = false;

    for (const plano of planoUsuario) {
      switch (plano) {
        case 'casa_apartamento':
          // Todos habilitados
          habilitado = true;
          break;

        case 'lote':
          if (!['mcmv', 'luxo', 'alto luxo'].includes(segNorm)) habilitado = true;
          break;

        case 'comercial':
          if (!['mcmv', 'alto luxo'].includes(segNorm)) habilitado = true;
          break;

        case 'compacto':
          if (segNorm !== 'mcmv') habilitado = true;
          break;

        default:
          break;
      }

      // se algum plano já habilitou, pode parar
      if (habilitado) break;
    }

    return { valor: seg, disabled: !habilitado };
  });

    return (
    <div className="filtros-container">
      <div className="linha-filtros">
        {/* Estados */}
        <div className="dropdown-column">
          <select
            className="dropdown"
            value={filtrosLocais.estado || ''}
            onChange={e => handleEstadoChange(e.target.value)}
          >
            <option value="">Selecione o estado</option>
            {estados.map(e => <option key={e.id} value={e.nome}>{e.nome}</option>)}
          </select>

          {/* Municípios */}
          <Select
            isMulti
            options={municipios.map(m => ({
              value: String(m.id),   // envia id
              label: m.nome_original // exibe nome_original
            }))}
            value={filtrosLocais.municipio?.map(id => {
              const municipioObj = municipios.find(m => String(m.id) === id);
              return municipioObj
                ? { value: String(municipioObj.id), label: municipioObj.nome_original }
                : null;
            }).filter(Boolean) as any}
            onChange={handleMunicipioChange}
            isDisabled={!filtrosLocais.estado}
          />

          {/* Bairros */}
          <Select
            isMulti
            options={bairros.map(b => ({
              value: String(b.id),
              label: b.nome_original
            }))}
            value={filtrosLocais.bairro?.map(id => {
              const bObj = bairros.find(b => String(b.id) === id);
              return bObj
                ? { value: String(bObj.id), label: bObj.nome_original }
                : null;
            }).filter(Boolean) as any}
            onChange={handleBairroChange}
            isDisabled={!filtrosLocais.municipio?.length}
          />
        </div>

        {/* Preço */}
        <FiltroRange
          label="Preço"
          expanded={expanded.preco}
          toggle={() => toggleExpand('preco')}
          min={filtrosLocais.precoMin}
          max={filtrosLocais.precoMax}
          setMin={(min:number|undefined)=>{
            setFiltrosLocais(prev => ({ ...prev, precoMin: min }));
            onChange({ precoMin: min });
          }}
          setMax={(max:number|undefined)=>{
            setFiltrosLocais(prev => ({ ...prev, precoMax: max }));
            onChange({ precoMax: max });
          }}
        />

        {/* Quartos */}
        <FiltroNumerico
          label="Quartos"
          expanded={expanded.quartos}
          toggle={() => toggleExpand('quartos')}
          valoresSelecionados={filtrosLocais.quartos || []}
          toggleValor={(v: number) => handleNumeroChange(v, 'quartos')}
          valores={filtrosPlanoMock.quartos}
          disabled={!habilitaQuartosSuites}
        />

        {/* Suítes */}
        <FiltroNumerico
          label="Suítes"
          expanded={expanded.suites}
          toggle={() => toggleExpand('suites')}
          valoresSelecionados={filtrosLocais.suites || []}
          toggleValor={(v: number) => handleNumeroChange(v, 'suites')}
          valores={filtrosPlanoMock.suites}
          disabled={!habilitaQuartosSuites}
        />

        {/* Tipo */}
        <FiltroTexto
          label="Tipo de produto"
          expanded={expanded.tipo}
          toggle={() => toggleExpand('tipo')}
          opcoes={tiposDisponiveis}
          selecionados={filtrosLocais.tipoSelecionado || []}
          toggleSelecionado={(v: string) => handleStringChange(v, 'tipoSelecionado')}
        />

        {/* Fase */}
        <FiltroTexto
          label="Fase do empreendimento"
          expanded={expanded.fase}
          toggle={() => toggleExpand('fase')}
          opcoes={filtrosPlanoMock.fase}
          selecionados={filtrosLocais.faseSelecionada || []}
          toggleSelecionado={(v: string) => handleStringChange(v, 'faseSelecionada')}
        />

        {/* Segmento */}
        <FiltroTexto
          label="Segmento"
          expanded={expanded.segmento}
          toggle={() => toggleExpand('segmento')}
          opcoes={segmentosDisponiveis}
          selecionados={filtrosLocais.segmentoSelecionado || []}
          toggleSelecionado={(v: string) => handleStringChange(v, 'segmentoSelecionado')}
        />

        {/* Área */}
        <FiltroRange
          label="Área (m²)"
          expanded={expanded.metragem}
          toggle={() => toggleExpand('metragem')}
          min={filtrosLocais.metragemMin}
          max={filtrosLocais.metragemMax}
          setMin={(min:number|undefined)=>{
            setFiltrosLocais(prev => ({ ...prev, metragemMin: min }));
            onChange({ metragemMin: min });
          }}
          setMax={(max:number|undefined)=>{
            setFiltrosLocais(prev => ({ ...prev, metragemMax: max }));
            onChange({ metragemMax: max });
          }}
        />
      </div>
    </div>
  );

};

// --- Componentes auxiliares ---
const FiltroRange = ({ label, expanded, toggle, min, max, setMin, setMax }: any) => (
  <div className="filtro-bloco">
    <span className="label">
      <button className="toggle-btn" onClick={toggle}>{expanded ? <ChevronUp /> : <ChevronDown />}</button>
      {label}
    </span>
    {expanded && (
      <div className="filtro-inline">
        <input type="number" placeholder="Mín" value={min ?? ''} onChange={e => setMin(e.target.value ? Number(e.target.value) : undefined)} />
        <input type="number" placeholder="Máx" value={max ?? ''} onChange={e => setMax(e.target.value ? Number(e.target.value) : undefined)} />
      </div>
    )}
  </div>
);

const FiltroNumerico = ({ label, expanded, toggle, valoresSelecionados, toggleValor, valores, disabled = false }: any) => (
  <div className="filtro-bloco">
    <span className="label">
      <button className="toggle-btn" onClick={toggle}>{expanded ? <ChevronUp /> : <ChevronDown />}</button>
      {label}
    </span>
    {expanded && (
      <div className="filtro-checkboxes">
        {valores.map((valor:number) => (
          <label key={valor} style={{ color: disabled ? 'gray' : 'inherit' }}>
            <input type="checkbox" disabled={disabled} checked={valoresSelecionados.includes(valor)} onChange={()=>toggleValor(valor)} />
            {valor === Math.max(...valores) ? `${valor}+` : valor}
          </label>
        ))}
      </div>
    )}
  </div>
);

const FiltroTexto = ({ label, expanded, toggle, opcoes, selecionados, toggleSelecionado }: any) => (
  <div className="filtro-bloco">
    <span className="label">
      <button className="toggle-btn" onClick={toggle}>{expanded ? <ChevronUp /> : <ChevronDown />}</button>
      {label}
    </span>
    {expanded && (
      <div className="filtro-checkboxes">
        {opcoes.map((opcao:any) => (
          <label key={opcao.valor || opcao}>
            <input type="checkbox" checked={selecionados.includes(opcao.valor || opcao)} 
              disabled={opcao.disabled || false}
              onChange={() => toggleSelecionado(opcao.valor || opcao)} 
            />
            {opcao.valor || opcao}
          </label>
        ))}
      </div>
    )}
  </div>
);

export default FiltrosLateraisEmpreendimentos;
